"use strict";
// packages/shared-schemas/src/leadClassification.ts
//
// Lead classification: five questions that replace the single flat
// `dealType` for routing, reporting and CRM write-back.
//
//   loanPurpose        one   what the money is for (purchase, refinance, ...)
//   propertyUse        one   owner occupied or investment
//   borrowerSituation  many  first home buyer, upgrader, self-employed, ...
//   loanStructure      one   standard residential, SMSF, commercial
//   loanAmount         one   a loan size band the tenant defines (under $500k,
//                            $5m or more, ...). A band, never a figure, so a
//                            rotation such as "$5m or more -> Damien" is an
//                            option id match like every other dimension.
//
// Options are per service category and subscriber-configurable on the
// taxonomy doc (`lead_taxonomy/default.classification`). When a category has
// no configured options, DEFAULT_CLASSIFICATION_OPTIONS apply to the
// mortgage category ('finance', and the legacy id 'home_loan').
//
// The legacy `dealType` keeps working both ways: an old dealType maps to a
// classification (LEGACY_DEAL_TYPE_MAP) and a classification renders back to
// the first matching dealType for readers that only know the old field.
//
// Pure: no I/O, no clock. Spec: docs/plans/2026-10-23-WEBSITE-FORMS-PLATFORM.md §2.4.
Object.defineProperty(exports, "__esModule", { value: true });
exports.LEGACY_DEAL_TYPE_MAP = exports.LeadClassificationInputSchema = exports.LeadClassificationSchema = exports.CLASSIFICATION_SERVICE_CATEGORY_ALIASES = exports.DEFAULT_CLASSIFICATION_SERVICE_CATEGORIES = exports.DEFAULT_CLASSIFICATION_OPTIONS = exports.LeadTaxonomyClassificationSchema = exports.ClassificationOptionsSchema = exports.ClassificationOptionSchema = exports.CLASSIFICATION_DIMENSION_QUESTIONS = exports.CLASSIFICATION_DIMENSION_LABELS = exports.CLASSIFICATION_FIELD_BY_DIMENSION = exports.DIMENSION_CARDINALITY = exports.ClassificationDimension = exports.CLASSIFICATION_DIMENSIONS = void 0;
exports.optionList = optionList;
exports.resolveClassificationOptions = resolveClassificationOptions;
exports.toPublicClassificationOptions = toPublicClassificationOptions;
exports.emptyClassification = emptyClassification;
exports.isEmptyClassification = isEmptyClassification;
exports.validateClassification = validateClassification;
exports.classificationLabels = classificationLabels;
exports.statedAnswersFromClassification = statedAnswersFromClassification;
exports.classificationFromLegacyDealType = classificationFromLegacyDealType;
exports.classificationImpliesLegacyDealType = classificationImpliesLegacyDealType;
exports.legacyDealTypeFromClassification = legacyDealTypeFromClassification;
const zod_1 = require("zod");
// Same identifier rule as the taxonomy (lowercase, digits, underscore).
const OPTION_ID_PATTERN = /^[a-z][a-z0-9_]{0,40}$/;
const OptionIdSchema = zod_1.z.string().regex(OPTION_ID_PATTERN, 'must be lowercase alphanumeric + underscore');
// ─── Dimensions ─────────────────────────────────────────────────────────────
exports.CLASSIFICATION_DIMENSIONS = ['loanPurpose', 'propertyUse', 'borrowerSituation', 'loanStructure', 'loanAmount'];
exports.ClassificationDimension = zod_1.z.enum(exports.CLASSIFICATION_DIMENSIONS);
/** How many values a lead may hold per dimension. */
exports.DIMENSION_CARDINALITY = {
    loanPurpose: 'one',
    propertyUse: 'one',
    borrowerSituation: 'many',
    loanStructure: 'one',
    loanAmount: 'one',
};
/** The lead field each dimension is stored in (the many-valued one is plural). */
exports.CLASSIFICATION_FIELD_BY_DIMENSION = {
    loanPurpose: 'loanPurpose',
    propertyUse: 'propertyUse',
    borrowerSituation: 'borrowerSituations',
    loanStructure: 'loanStructure',
    loanAmount: 'loanAmount',
};
/** Short labels used in notes, emails and CRM write-back. */
exports.CLASSIFICATION_DIMENSION_LABELS = {
    loanPurpose: 'Loan purpose',
    propertyUse: 'Property use',
    borrowerSituation: 'Situation',
    loanStructure: 'Loan structure',
    loanAmount: 'Loan amount',
};
/** Question wording for forms (website SDK, manual lead form). */
exports.CLASSIFICATION_DIMENSION_QUESTIONS = {
    loanPurpose: 'What is the loan for?',
    propertyUse: 'How will the property be used?',
    borrowerSituation: 'Which of these describe you?',
    loanStructure: 'What kind of loan is it?',
    loanAmount: 'Roughly how much do you need to borrow?',
};
// ─── Options ────────────────────────────────────────────────────────────────
exports.ClassificationOptionSchema = zod_1.z.object({
    id: OptionIdSchema,
    label: zod_1.z.string().trim().min(1).max(80),
    order: zod_1.z.number().int().min(0).max(999),
    active: zod_1.z.boolean().default(true),
});
/**
 * One service category's options, every dimension present (empty = not asked).
 * Stored taxonomy docs written before a dimension existed lack its key; every
 * reader below treats a missing list as empty (optionList), so such a tenant
 * is simply not asked that question until it adds options.
 */
exports.ClassificationOptionsSchema = zod_1.z
    .object({
    loanPurpose: zod_1.z.array(exports.ClassificationOptionSchema).max(50).default([]),
    propertyUse: zod_1.z.array(exports.ClassificationOptionSchema).max(50).default([]),
    borrowerSituation: zod_1.z.array(exports.ClassificationOptionSchema).max(50).default([]),
    loanStructure: zod_1.z.array(exports.ClassificationOptionSchema).max(50).default([]),
    loanAmount: zod_1.z.array(exports.ClassificationOptionSchema).max(50).default([]),
})
    .superRefine((opts, ctx) => {
    for (const dim of exports.CLASSIFICATION_DIMENSIONS) {
        const seen = new Set();
        optionList(opts, dim).forEach((o, i) => {
            if (seen.has(o.id)) {
                ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: [dim, i, 'id'], message: `Duplicate option id '${o.id}'` });
            }
            seen.add(o.id);
        });
    }
});
/** The taxonomy doc field: serviceCategoryId -> that category's options. */
exports.LeadTaxonomyClassificationSchema = zod_1.z.record(zod_1.z.string().regex(OPTION_ID_PATTERN), exports.ClassificationOptionsSchema);
/** A category's options for one dimension; [] when the stored doc predates the dimension. */
function optionList(options, dim) {
    const list = options?.[dim];
    return Array.isArray(list) ? list : [];
}
function opts(rows) {
    return rows.map(([id, label], i) => ({ id, label, order: i + 1, active: true }));
}
/**
 * Defaults for the mortgage category. Applied to 'finance' and the legacy
 * 'home_loan' id when the taxonomy doc carries no classification for them.
 */
exports.DEFAULT_CLASSIFICATION_OPTIONS = Object.freeze({
    loanPurpose: opts([
        ['purchase', 'Purchase'],
        ['refinance', 'Refinance'],
        ['construction', 'Construction'],
        ['equity_release', 'Equity release / top-up'],
        ['debt_consolidation', 'Debt consolidation'],
        ['bridging', 'Bridging'],
    ]),
    propertyUse: opts([
        ['owner_occupied', 'Owner occupied'],
        ['investment', 'Investment'],
    ]),
    borrowerSituation: opts([
        ['first_home_buyer', 'First home buyer'],
        ['upgrader', 'Upgrader'],
        ['downsizer', 'Downsizer'],
        ['self_employed', 'Self-employed'],
        ['rentvesting', 'Rentvesting'],
    ]),
    loanStructure: opts([
        ['standard_residential', 'Standard residential'],
        ['smsf', 'SMSF'],
        ['commercial', 'Commercial / business'],
    ]),
    loanAmount: opts([
        // Option ids must start with a letter (OPTION_ID_PATTERN).
        ['under_500k', 'Under $500k'],
        ['from_500k_to_1m', '$500k to $1m'],
        ['from_1m_to_2m', '$1m to $2m'],
        ['from_2m_to_5m', '$2m to $5m'],
        ['from_5m', '$5m or more'],
    ]),
});
/** Service categories the defaults apply to. 'home_loan' is the legacy id for 'finance'. */
exports.DEFAULT_CLASSIFICATION_SERVICE_CATEGORIES = ['finance', 'home_loan'];
/** Legacy category ids that read another category's configured options. */
exports.CLASSIFICATION_SERVICE_CATEGORY_ALIASES = { home_loan: 'finance' };
/**
 * The options that apply to a lead in `serviceCategory`, or null when the
 * category asks no classification questions. Order: the category's own
 * configured options; for an alias, its target's configured options; then
 * the defaults for the mortgage category.
 */
function resolveClassificationOptions(taxonomyClassification, serviceCategory) {
    if (!serviceCategory)
        return null;
    const own = taxonomyClassification?.[serviceCategory];
    if (own)
        return own;
    const alias = exports.CLASSIFICATION_SERVICE_CATEGORY_ALIASES[serviceCategory];
    if (alias && taxonomyClassification?.[alias])
        return taxonomyClassification[alias];
    if (exports.DEFAULT_CLASSIFICATION_SERVICE_CATEGORIES.includes(serviceCategory))
        return exports.DEFAULT_CLASSIFICATION_OPTIONS;
    return null;
}
/** Public projection for the website config response: active options only, in order, id + label. */
function toPublicClassificationOptions(options) {
    if (!options)
        return null;
    const out = {};
    for (const dim of exports.CLASSIFICATION_DIMENSIONS) {
        out[dim] = activeSorted(optionList(options, dim)).map((o) => ({ id: o.id, label: o.label }));
    }
    return out;
}
function activeSorted(list) {
    return (list ?? [])
        .filter((o) => o.active !== false)
        .slice()
        .sort((a, b) => a.order - b.order || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}
// ─── Lead value ─────────────────────────────────────────────────────────────
exports.LeadClassificationSchema = zod_1.z.object({
    loanPurpose: OptionIdSchema.nullable(),
    propertyUse: OptionIdSchema.nullable(),
    borrowerSituations: zod_1.z.array(OptionIdSchema).max(20),
    loanStructure: OptionIdSchema.nullable(),
    // Optional: classifications stored before the dimension existed carry no
    // key. Readers use `c.loanAmount ?? null`; new values always write it.
    loanAmount: OptionIdSchema.nullable().optional(),
});
/**
 * What a form or webhook may send. Everything optional; values are checked
 * against the tenant's options by validateClassification, never trusted.
 * Strings are bounded but not pattern-checked here, so an unknown value is
 * reported by validateClassification rather than failing the whole request.
 */
exports.LeadClassificationInputSchema = zod_1.z.object({
    loanPurpose: zod_1.z.string().max(64).nullable().optional(),
    propertyUse: zod_1.z.string().max(64).nullable().optional(),
    borrowerSituations: zod_1.z.array(zod_1.z.string().max(64)).max(20).optional(),
    loanStructure: zod_1.z.string().max(64).nullable().optional(),
    loanAmount: zod_1.z.string().max(64).nullable().optional(),
});
/** A fresh empty classification (a new object each call, safe to mutate). */
function emptyClassification() {
    return { loanPurpose: null, propertyUse: null, borrowerSituations: [], loanStructure: null, loanAmount: null };
}
/** True when no dimension holds a value. */
function isEmptyClassification(c) {
    return !c || (!c.loanPurpose && !c.propertyUse && !c.loanStructure && !c.loanAmount && (c.borrowerSituations ?? []).length === 0);
}
/**
 * Check a submitted classification against the options in force. Values that
 * are not an active option of their dimension are dropped from `value` and
 * reported in `unknown` as `dimension:value`, so the caller can log them and
 * still accept the lead. With no options (the category asks nothing) every
 * supplied value is unknown. Situations come back de-duplicated in option
 * order, so the same answers always produce the same stored value.
 */
function validateClassification(input, options) {
    const value = emptyClassification();
    const unknown = [];
    if (!input)
        return { value, unknown };
    const allowed = (dim) => {
        const m = new Map();
        if (!options)
            return m;
        activeSorted(optionList(options, dim)).forEach((o, i) => m.set(o.id, i));
        return m;
    };
    const single = (dim) => {
        const raw = input[dim];
        if (raw === null || raw === undefined)
            return null;
        const v = String(raw).trim();
        if (v === '')
            return null;
        if (allowed(dim).has(v))
            return v;
        unknown.push(`${dim}:${v}`);
        return null;
    };
    value.loanPurpose = single('loanPurpose');
    value.propertyUse = single('propertyUse');
    value.loanStructure = single('loanStructure');
    value.loanAmount = single('loanAmount');
    const situations = allowed('borrowerSituation');
    const kept = new Set();
    for (const raw of input.borrowerSituations ?? []) {
        const v = String(raw).trim();
        if (v === '')
            continue;
        if (situations.has(v))
            kept.add(v);
        else
            unknown.push(`borrowerSituation:${v}`);
    }
    value.borrowerSituations = [...kept].sort((a, b) => (situations.get(a) ?? 0) - (situations.get(b) ?? 0));
    return { value, unknown: [...new Set(unknown)] };
}
/**
 * Labels for a stored classification, for notes, emails and CRM write-back.
 * A value with no matching option (renamed or removed since) shows its id
 * rather than disappearing.
 */
function classificationLabels(c, options) {
    const label = (dim, id) => {
        if (!id)
            return null;
        return optionList(options, dim).find((o) => o.id === id)?.label ?? id;
    };
    return {
        loanPurpose: label('loanPurpose', c?.loanPurpose ?? null),
        propertyUse: label('propertyUse', c?.propertyUse ?? null),
        borrowerSituations: (c?.borrowerSituations ?? []).map((id) => label('borrowerSituation', id)),
        loanStructure: label('loanStructure', c?.loanStructure ?? null),
        loanAmount: label('loanAmount', c?.loanAmount ?? null),
    };
}
/**
 * The answered dimensions of `c` as label pairs, in dimension order; blank
 * dimensions are left out. Frozen onto a website referral at submission so
 * the Partner Network desk shows the wording the visitor saw, even after the
 * tenant renames or removes an option.
 */
function statedAnswersFromClassification(c, options) {
    const labels = classificationLabels(c, options);
    const answers = {
        loanPurpose: labels.loanPurpose,
        propertyUse: labels.propertyUse,
        borrowerSituation: labels.borrowerSituations.length > 0 ? labels.borrowerSituations.join(', ') : null,
        loanStructure: labels.loanStructure,
        loanAmount: labels.loanAmount,
    };
    return exports.CLASSIFICATION_DIMENSIONS.flatMap((dim) => answers[dim] ? [{ question: exports.CLASSIFICATION_DIMENSION_LABELS[dim], answer: answers[dim] }] : []);
}
/**
 * Legacy dealType -> classification. Iteration order is the order
 * legacyDealTypeFromClassification checks, so it is part of the contract.
 */
exports.LEGACY_DEAL_TYPE_MAP = Object.freeze({
    fhb: { dimension: 'borrowerSituation', value: 'first_home_buyer' },
    upgrader: { dimension: 'borrowerSituation', value: 'upgrader' },
    downsizer: { dimension: 'borrowerSituation', value: 'downsizer' },
    smsf: { dimension: 'loanStructure', value: 'smsf' },
    investment: { dimension: 'propertyUse', value: 'investment' },
    commercial: { dimension: 'loanStructure', value: 'commercial' },
    refinance: { dimension: 'loanPurpose', value: 'refinance' },
});
/** The classification an old dealType stands for, or null when the dealType has no mapping. */
function classificationFromLegacyDealType(dealType) {
    if (!dealType)
        return null;
    const m = Object.prototype.hasOwnProperty.call(exports.LEGACY_DEAL_TYPE_MAP, dealType) ? exports.LEGACY_DEAL_TYPE_MAP[dealType] : undefined;
    if (!m)
        return null;
    const c = emptyClassification();
    if (m.dimension === 'borrowerSituation')
        c.borrowerSituations = [m.value];
    else
        c[m.dimension] = m.value;
    return c;
}
/** True when `c` holds the value the legacy dealType stands for. */
function classificationImpliesLegacyDealType(c, dealType) {
    if (!c || !dealType)
        return false;
    const m = Object.prototype.hasOwnProperty.call(exports.LEGACY_DEAL_TYPE_MAP, dealType) ? exports.LEGACY_DEAL_TYPE_MAP[dealType] : undefined;
    if (!m)
        return false;
    if (m.dimension === 'borrowerSituation')
        return (c.borrowerSituations ?? []).includes(m.value);
    return c[m.dimension] === m.value;
}
/**
 * The first legacy dealType a classification satisfies, in LEGACY_DEAL_TYPE_MAP
 * order. For display and old readers only: it loses information by design.
 */
function legacyDealTypeFromClassification(c) {
    if (!c)
        return null;
    for (const dealType of Object.keys(exports.LEGACY_DEAL_TYPE_MAP)) {
        if (classificationImpliesLegacyDealType(c, dealType))
            return dealType;
    }
    return null;
}
